import { ArrowLeft, Check, ChevronDown, ChevronsUpDown, Pencil, Plus, Search, X } from "lucide-react";
import { Button } from "../../Components/UI/button";
import { Input } from "../UI/input";
import { cn } from "../../Lib/Cn";
import { Popover, PopoverTrigger } from "../UI/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../Components/UI/dialog";
import { RiResetLeftFill } from "react-icons/ri";
import { useCallback, useEffect, useRef, useState } from "react";


const MobileFullScreenDropdown = ({
    options = [],
    value,
    onSelect,
    placeholder = "Select option",
    displayKey = "label",
    valueKey = "value",
    iconKey = "icon", // Key for icon component in options
    allowAdd = false,
    className = "",
    disabled = false,
    emptyMessage = "No options found",
    fieldID,
    label,
    searchThreshold = 10,
    searchPlaceholder = "Search...",
    showDefaultBox = false,
    showClearButton = true,
    isfilter = false,
    allchange,
    displayValue,
    resetButton = false,
    mobileDropdownText,
    handleReset,
    isIcon = false,
    isSorting = true,
    buttonTextCss = "",
    showAllOption = false,
    sortDescending = false,
    allText,
    allID,
    prependOnlyName,
    renderOption = null, // New prop for custom option rendering
    showButtonStyle = false, // New prop to show button with text and chevron
    buttonTitle = "Add", // New prop for button title
    enableMultiFieldSearch = false, // Enable multi-field search functionality
    externalOpen = false, // External open state
    onExternalOpenChange = null, // Callback for external open state changes
    showEditIcon = false, // Show edit icon for options
    onEditClick = null, // Callback when edit icon is clicked
    editPermissionCheck = null, // Function to check if user can edit specific option
    addText = "",
    userClearedRef,
    showAddInHeader = false,
    openDialog = false,
    onClose = null,
    isMulti = false,
    hideSelectedChips = false,
    MultiSelect = false,
    clearSearchOnSelect = true
}) => {
    const isMultiMode = isMulti || MultiSelect;
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [viewportHeight, setViewportHeight] = useState(0);
    const searchInputRef = useRef(null);
    const modalRef = useRef(null);

    const isOpen = onExternalOpenChange ? externalOpen : internalIsOpen;
    const setIsOpen = onExternalOpenChange ? onExternalOpenChange : setInternalIsOpen;

    useEffect(() => {
        if (openDialog) {
            openModal();
        }
    }, [openDialog]);

    const allTextName = allText || "All";
    const allIDValue = allID || "all";

    // Get viewport height for iOS
    const updateViewportHeight = useCallback(() => {
        setViewportHeight(window.innerHeight);
    }, []);

    useEffect(() => {
        updateViewportHeight();
        window.addEventListener('resize', updateViewportHeight);
        window.addEventListener('orientationchange', updateViewportHeight);

        return () => {
            window.removeEventListener('resize', updateViewportHeight);
            window.removeEventListener('orientationchange', updateViewportHeight);
        };
    }, [updateViewportHeight]);

    const modifiedOptions = prependOnlyName
        ? [allTextName, ...options]
        : showAllOption
            ? [{ [valueKey]: allIDValue, [displayKey]: allTextName }, ...options]
            : options;

    const sortedOptions = isSorting
        ? [...modifiedOptions].sort((a, b) => {
            const aValue = typeof a === "string" ? a : a?.[valueKey] ?? "";
            const bValue = typeof b === "string" ? b : b?.[valueKey] ?? "";
            const aLabel = typeof a === "string" ? a : a?.[displayKey] ?? "";
            const bLabel = typeof b === "string" ? b : b?.[displayKey] ?? "";

            if (aValue === "all") return -1;
            if (bValue === "all") return 1;

            return sortDescending
                ? bLabel.localeCompare(aLabel)
                : aLabel.localeCompare(bLabel);
        })
        : [...modifiedOptions];


    const showSearch = sortedOptions.length > searchThreshold;

    const getSelectedOptionLabel = () => {
        if (isMultiMode) {
            if (!value || value.length === 0) return placeholder;
            return value.map(val => {
                const selectedOption = sortedOptions.find(option => {
                    const optionValue = typeof option === 'string' ? option : option[valueKey];
                    return optionValue === val;
                });
                return selectedOption ? (typeof selectedOption === 'string' ? selectedOption : selectedOption[displayKey]) : val;
            }).join(', ');
        }
        if (!value) return placeholder;
        const selectedOption = sortedOptions.find(option => {
            const optionValue = typeof option === 'string' ? option : option[valueKey];
            return optionValue === value;
        });
        return selectedOption ? (typeof selectedOption === 'string' ? selectedOption : selectedOption[displayKey]) : placeholder;
    };

    // Get selected option icon component
    const getSelectedOptionIcon = () => {
        if (isMultiMode || !value) return null;
        const selectedOption = sortedOptions.find(option => {
            const optionValue = typeof option === 'string' ? option : option[valueKey];
            return optionValue === value;
        });
        return selectedOption && typeof selectedOption === 'object' ? selectedOption[iconKey] : null;
    };

    const selectedLabel = getSelectedOptionLabel();
    const selectedIcon = getSelectedOptionIcon();

    const getDisplay = () => {
        if (!value || value === "all") return label;
        if (displayValue) return displayValue(value);
        return typeof value === "object" ? value.name : value;
    };

    const filteredOptions = showSearch && searchTerm
        ? sortedOptions.filter((option) => {
            if (typeof option === "string") {
                return option.toLowerCase().includes(searchTerm.toLowerCase().trim());
            }
            const optionLabel = option[displayKey] || "";
            const optionAgency = option.agencyName || option.AgencyName || option.fullAgencyName || "";
            const optionSearchable = option.searchableText || "";
            const term = searchTerm.toLowerCase().trim();

            if (enableMultiFieldSearch && optionSearchable) {
                return optionSearchable.toLowerCase().includes(term);
            }

            // Search across label, agency name, and searchableText
            const combinedSearchText = `${optionLabel} ${optionAgency} ${optionSearchable}`.toLowerCase();
            return combinedSearchText.includes(term);
        })
        : sortedOptions;

    const handleSelect = (selectedValue, selectedOption = null) => {
        if (selectedValue === "add-new-item") {
            onSelect("add", null, searchTerm);
            closeModal();
        } else {
            const option = selectedOption || sortedOptions.find((opt) => {
                const optionValue = typeof opt === "string" ? opt : opt[valueKey];
                return optionValue === selectedValue;
            });
            if (isMultiMode) {
                const arr = Array.isArray(value) ? value : [];
                const newValue = arr.includes(selectedValue)
                    ? arr.filter(v => v !== selectedValue)
                    : [...arr, selectedValue];
                onSelect(newValue, option, searchTerm);
                if (clearSearchOnSelect) {
                    setSearchTerm("");
                }
            } else {
                onSelect(selectedValue, option, searchTerm);
                closeModal();
            }
        }
    };

    const openModal = () => {
        setIsOpen(true);
        setSearchTerm("");
        // Prevent body scroll
        document.body.style.position = 'fixed';
        document.body.style.top = `-${window.scrollY}px`;
        document.body.style.width = '100%';
    };

    const closeModal = () => {
        setIsOpen(false);
        setSearchTerm("");
        // Restore body scroll
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
    };

    useEffect(() => {
        if (isOpen) {
            if (showSearch && searchInputRef.current) {
                // Delay focus to ensure modal is fully rendered
                const timer = setTimeout(() => {
                    searchInputRef.current?.focus();
                }, 300);
                return () => clearTimeout(timer);
            }
            if (options.length === 0) {
                handleSelect('add-new-item');
            }
        }
    }, [isOpen, showSearch]);

    // Handle iOS keyboard
    useEffect(() => {
        if (!isOpen) return;

        const handleTouchMove = (e) => {
            if (modalRef.current && !modalRef.current.contains(e.target)) {
                e.preventDefault();
            }
        };

        document.addEventListener('touchmove', handleTouchMove, { passive: false });

        return () => {
            document.removeEventListener('touchmove', handleTouchMove);
        };
    }, [isOpen]);

    return (
        <div className={`relative w-full ${className}`}>
            {/* Trigger Button */}
            {isIcon ? (
                <Plus
                    className={`h-5 w-5 text-gray-600 hover:text-gray-800 cursor-pointer transition-colors ${className}`}
                    onClick={openModal}
                    data-transaction-dropdown-trigger
                />
            ) : showButtonStyle ? (
                <Button
                    onClick={openModal}
                    disabled={disabled}
                    className={`${className} flex items-center gap-2`}
                >
                    <Plus className="h-4 w-4" />
                    {buttonTitle}
                    <ChevronDown className="h-4 w-4" />
                </Button>
            ) : showDefaultBox ? (
                <Popover>
                    <div
                        className={cn(
                            "relative inline-flex items-center bg-white rounded-[5px] !h-8 lg:!h-10 w-fit",
                            value && value !== "all" && "bg-white border border-[var(--sahi-green)]"
                        )}
                    >
                        <PopoverTrigger asChild>
                            <Button
                                onClick={openModal}
                                id={fieldID}
                                variant="semidefault"
                                className={cn(
                                    "w-auto max-w-[220px] bg-white justify-between text-black rounded-[5px] !h-8 lg:!h-10",
                                    (!value || value === "all") ? "filter-border" : "!border-none",
                                    value && value !== "all" && "bg-[#0b5d1e24] rounded-r-none",
                                )}
                            >
                                <span className="truncate font-normal flex items-center gap-2">
                                    {getDisplay()}
                                    {selectedIcon && <span className="flex-shrink-0">{selectedIcon}</span>}
                                </span>
                                <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                            </Button>
                        </PopoverTrigger>

                        {showClearButton && value && value !== "all" && (
                            <div
                                className={cn(
                                    "inline-flex items-center justify-center h-10 w-[35px] bg-[#0b5d1e24] p-1",
                                    "border-l border-t-0 border-r-0 border-b-0 border-[var(--sahi-green)]",
                                    "rounded-r-md rounded-l-none !h-8 lg:!h-10"
                                )}
                            >
                                <div className="w-10 h-7 flex items-center justify-center">
                                    <X
                                        className="h-6 w-4 text-black cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            userClearedRef.current = true;
                                            allchange("all");
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </Popover>
            ) : (
                <div
                    id={fieldID}
                    onClick={() => {
                        if (!disabled) openModal();
                    }}
                    disabled={disabled}
                    className={cn(
                        `w-full min-h-[44px] px-3 py-0 pr-1 bg-white border border-input sahi-radius text-left text-sm shadow-xs`,
                        `focus:outline-none focus:ring-1 focus:ring-sahi-primary-color focus:border-sahi-primary-color`,
                        `flex items-center justify-between hover:border-gray-400 disabled:cursor-not-allowed disabled:opacity-50`,
                        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                        buttonTextCss
                    )}
                >
                    {isMultiMode ? (
                        <div className="flex flex-wrap gap-1.5 items-center w-full py-1 text-sm overflow-hidden flex-1 select-none">
                            {MultiSelect ? (
                                (() => {
                                    const arr = Array.isArray(value) ? value : [];
                                    if (arr.length === 0) {
                                        return <span className="text-muted-foreground">{placeholder}</span>;
                                    }
                                    if (arr.length === 1) {
                                        const opt = sortedOptions.find(option => {
                                            const optionValue = typeof option === 'string' ? option : option[valueKey];
                                            return optionValue === arr[0];
                                        });
                                        const name = opt ? (typeof opt === 'string' ? opt : opt[displayKey]) : arr[0];
                                        return <span className="text-text-color font-medium truncate">{name}</span>;
                                    }

                                    // Avatar stack for 2+ selected attendees
                                    const getInitials = (nameStr) => {
                                        if (!nameStr) return '';
                                        const str = String(nameStr);
                                        const parts = str.trim().split(/\s+/);
                                        if (parts.length === 1) {
                                            return parts[0].substring(0, 2).toUpperCase();
                                        }
                                        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
                                    };

                                    const attendeesToShow = arr.slice(0, 4);
                                    const extraCount = arr.length - attendeesToShow.length;

                                    return (
                                        <div className="flex items-center gap-2 py-0.5">
                                            {/* Avatar Stack */}
                                            <div className="flex -space-x-2 overflow-hidden items-center">
                                                {attendeesToShow.map(attendeeID => {
                                                    const opt = sortedOptions.find(option => {
                                                        const optionValue = typeof option === 'string' ? option : option[valueKey];
                                                        return optionValue === attendeeID;
                                                    });
                                                    const name = opt ? (typeof opt === 'string' ? opt : opt[displayKey]) : attendeeID;
                                                    const initials = getInitials(name);
                                                    return (
                                                        <div
                                                            key={attendeeID}
                                                            className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-white text-white text-[10px] font-semibold shrink-0 select-none shadow-xs"
                                                            style={{ backgroundColor: '#0b5d1e' }}
                                                            title={name}
                                                        >
                                                            {initials}
                                                        </div>
                                                    );
                                                })}
                                                {extraCount > 0 && (
                                                    <div className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-white bg-zinc-200 text-zinc-700 text-[10px] font-semibold shrink-0 select-none shadow-xs">
                                                        +{extraCount}
                                                    </div>
                                                )}
                                            </div>
                                            {/* Count Label */}
                                            <span className="text-sm font-semibold text-text-color">
                                                {arr.length} {label?.toLowerCase() || 'selected'}
                                            </span>
                                        </div>
                                    );
                                })()
                            ) : hideSelectedChips ? (
                                <span className="text-muted-foreground">{placeholder}</span>
                            ) : (
                                (() => {
                                    const opts = [];
                                    const arr = Array.isArray(value) ? value : [];
                                    arr.forEach(val => {
                                        const opt = sortedOptions.find(option => {
                                            const optionValue = typeof option === 'string' ? option : option[valueKey];
                                            return optionValue === val;
                                        });
                                        if (opt) {
                                            opts.push({
                                                value: val,
                                                label: typeof opt === 'string' ? opt : opt[displayKey]
                                            });
                                        }
                                    });
                                    return opts.map(opt => (
                                        <span
                                            key={opt.value}
                                            className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-800 text-xs font-semibold px-2 py-1 rounded border border-zinc-200"
                                        >
                                            {opt.label}
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const newValue = arr.filter(v => v !== opt.value);
                                                    onSelect(newValue);
                                                }}
                                                className="hover:bg-zinc-200 rounded-full p-0.5 text-zinc-500 hover:text-zinc-800 focus:outline-none touch-manipulation"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </span>
                                    ));
                                })()
                            )}
                            {!MultiSelect && !hideSelectedChips && (!value || value.length === 0) && (
                                <span className="text-muted-foreground">{placeholder}</span>
                            )}
                        </div>
                    ) : (
                        <span className={cn(
                            "flex items-center gap-2 min-w-0 flex-1",
                            (!value || selectedLabel === placeholder) ? "text-muted-foreground" : "text-gray-900"
                        )}>
                            <span className="truncate">{selectedLabel}</span>
                            {selectedIcon && <span className="flex-shrink-0">{selectedIcon}</span>}
                        </span>
                    )}
                    <div className="flex items-center gap-0 ml-2">
                        <ChevronDown
                            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""} mr-2`}
                        />
                        {value && showEditIcon && onEditClick && editPermissionCheck && editPermissionCheck(sortedOptions.find(opt => {
                            const optionValue = typeof opt === "string" ? opt : opt[valueKey];
                            return optionValue === value;
                        })) == true ? (
                            <div
                                className={cn(
                                    "inline-flex items-center justify-center h-10 w-[30px] p-1",
                                    "border-l border-t-0 border-r-0 border-b-0 border-gray-300 cursor-pointer"
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const option = sortedOptions.find(opt => {
                                        const optionValue = typeof opt === "string" ? opt : opt[valueKey];
                                        return optionValue === value;
                                    });
                                    onEditClick(option);
                                }}
                            >
                                <div className="w-10 h-7 flex items-center justify-center">
                                    <Pencil className="h-4 w-4 text-gray-600" />
                                </div>
                            </div>
                        ) : null}
                        {(showClearButton && value && value !== "all") == true ? (
                            <div
                                className={cn(
                                    "inline-flex items-center justify-center h-10 w-[30px] p-1",
                                    "border-l border-t-0 border-r-0 border-b-0 border-gray-300 cursor-pointer"
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    userClearedRef.current = true;
                                    allchange("all");
                                }}
                            >
                                <div className="w-10 h-7 flex items-center justify-center">
                                    <X
                                        className="h-4 w-4 text-gray-600"
                                    />
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            )}

            {/* Mobile Dialog */}
            <Dialog open={isOpen} onOpenChange={closeModal}>
                <DialogContent
                    className="flex flex-col rounded-none min-h-screen w-full max-w-full border-0 p-0 sm:!max-w-[450px] sm:min-h-0 sm:max-h-[85vh] sm:h-auto sm:rounded-lg !z-[100]"
                    style={{
                        height: window.innerWidth < 640 ? (viewportHeight || '100vh') : 'auto'
                    }}
                >
                    <div
                        ref={modalRef}
                        className="relative bg-sahi-primary-color shadow-xl w-full flex flex-col h-[100dvh] sm:h-auto sm:max-h-[85vh] sm:rounded-lg"
                    >
                        <DialogHeader className="!px-4 !py-3">
                            <div className="flex justify-start items-center gap-3">
                                <DialogClose
                                    className="p-1 hover:bg-gray-100 hover:bg-opacity-20 touch-manipulation"
                                    onClick={() => {
                                        closeModal();
                                        onClose?.();
                                    }}
                                >
                                    <ArrowLeft className="w-6 h-6 " />
                                </DialogClose>
                            </div>
                            <div className="flex items-center justify-center flex-1">
                                <DialogTitle className="text-lg font-medium">
                                    {mobileDropdownText || label || "Select Option"}
                                </DialogTitle>
                            </div>
                            <div className="flex items-center justify-end" style={{ width: '44px' }}>
                                {(showAddInHeader && allowAdd) && (
                                    <button
                                        onClick={() => handleSelect("add-new-item")}
                                        className="p-1 hover:bg-gray-100 hover:bg-opacity-20 touch-manipulation rounded-full"
                                    >
                                        <Plus className="w-6 h-6" />
                                    </button>
                                )}
                            </div>
                        </DialogHeader>

                        {/* Search - Fixed height */}
                        {showSearch && (
                            <div
                                className="px-4 py-3 bg-white border-b border-gray-200 flex-shrink-0"
                                style={{ minHeight: '70px' }}
                            >
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <Input
                                        ref={searchInputRef}
                                        type="text"
                                        placeholder={searchPlaceholder}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm placeholder-gray-500 
                          focus:ring-2 focus:ring-sahi-primary-color touch-manipulation"
                                        style={{
                                            fontSize: '16px', // Prevents zoom on iOS
                                            minHeight: '44px'
                                        }}
                                        autoComplete="off"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck="false"
                                    />
                                    {searchTerm && (
                                        <button
                                            onClick={() => setSearchTerm("")}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full touch-manipulation"
                                            style={{ minHeight: '32px', minWidth: '32px' }}
                                        >
                                            <X className="w-4 h-4 text-gray-600" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Options - Scrollable area */}
                        <div
                            className="flex-1 min-h-0 bg-gray-50"
                            style={{
                                overflowY: 'auto',
                                WebkitOverflowScrolling: 'touch',
                                transform: 'translateZ(0)' // Hardware acceleration
                            }}
                        >
                            <div className="py-0">
                                {filteredOptions.map((option, index) => {
                                    const optionValue = typeof option === "string" ? option : option[valueKey];
                                    const optionLabel = typeof option === "string" ? option : option[displayKey];
                                    const optionIcon = typeof option === "object" ? option[iconKey] : null;
                                    const isSelected = isMulti
                                        ? (Array.isArray(value) && value.includes(optionValue))
                                        : optionValue === value;

                                    return (
                                        <div
                                            key={`${optionValue}-${index}`}
                                            className={cn(
                                                "w-full flex items-center justify-between px-5 text-sm border-b touch-manipulation",
                                                isSelected
                                                    ? "bg-gray-200 text-sahi-primary-color font-medium"
                                                    : "hover:bg-gray-50 text-gray-800 active:bg-gray-100"
                                            )}
                                            style={{
                                                minHeight: '44px' // iOS touch target minimum
                                            }}
                                        >
                                            <div
                                                className="cursor-pointer flex-1 min-w-0"
                                                onClick={() => handleSelect(optionValue, option)}
                                            >
                                                {renderOption ? (
                                                    <div className="flex items-center flex-1">
                                                        {renderOption(option, isSelected)}
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center justify-between gap-2">
                                                        <div className="flex items-center min-w-0">
                                                            {isMulti && (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    readOnly
                                                                    className="mr-3 h-4 w-4 rounded border-gray-300 text-sahi-primary-color accent-sahi-primary-color shrink-0 cursor-pointer"
                                                                />
                                                            )}
                                                            <span className="truncate">
                                                                {optionLabel}
                                                                {typeof option === 'object' && option?.agencyName && (
                                                                    <span className="opacity-50 text-xs ml-1.5 font-normal">
                                                                        ({option.agencyName})
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </div>
                                                        {optionIcon && <span className="flex-shrink-0">{optionIcon}</span>}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 ml-3">
                                                {!isMulti && isSelected && <Check className="w-5 h-5 text-sahi-primary-color flex-shrink-0" />}
                                            </div>
                                        </div>
                                    );
                                })}

                                {filteredOptions.length === 0 && (
                                    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                            <Search className="w-6 h-6 text-gray-400" />
                                        </div>
                                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                                            {/* {searchTerm ? "No results found" : emptyMessage} */}
                                            {emptyMessage}
                                        </h3>
                                        {allowAdd && (
                                            <Button
                                                onClick={() => handleSelect("add-new-item")}
                                                className="touch-manipulation"
                                                style={{ minHeight: '44px' }}
                                            >
                                                <Plus className="h-4 w-4" />
                                                {addText ? addText : `Add ${label || ""}`}
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer - Fixed height */}
                        <div
                            className="sticky bottom-0 px-4 py-3 bg-sahi-disable border-t border-gray-200 flex-shrink-0"
                            style={{ minHeight: '70px' }}
                        >
                            <div className="flex items-center justify-start gap-2 flex-wrap w-full">
                                {isMulti ? (
                                    <Button
                                        onClick={() => {
                                            closeModal();
                                            onClose?.();
                                        }}
                                        className="w-full bg-sahi-green border hover:bg-sahi-green/90 text-primary-foreground touch-manipulation"
                                        style={{ minHeight: '44px' }}
                                    >
                                        <Check className="h-4 w-4" />
                                        Select ({Array.isArray(value) ? value.length : 0} Selected)
                                    </Button>
                                ) : (
                                    <>
                                        {allowAdd && (
                                            <Button
                                                onClick={() => handleSelect("add-new-item")}
                                                className="touch-manipulation"
                                                style={{ minHeight: '44px' }}
                                            >
                                                <Plus className="h-4 w-4" />
                                                {addText ? addText : `Add ${label || ""}`}
                                            </Button>
                                        )}
                                        {resetButton && (
                                            <Button
                                                onClick={() => {
                                                    handleReset();
                                                    closeModal();
                                                }}
                                                className="touch-manipulation"
                                                style={{ minHeight: '44px' }}
                                            >
                                                <RiResetLeftFill className="w-4 h-4" />
                                                Reset
                                            </Button>
                                        )}
                                        <Button
                                            variant="secondary"
                                            onClick={() => {
                                                closeModal();
                                                onClose?.();
                                            }}
                                            className="touch-manipulation"
                                            style={{ minHeight: '44px' }}
                                        >
                                            <X className="h-4 w-4" />
                                            Close
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MobileFullScreenDropdown;